import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminGetContent, adminUpdateContent, adminResetContent } from '../../lib/api';
import { useContent } from '../../contexts/ContentContext';
import { useToast } from '../../hooks/use-toast';
import { Loader2, ArrowLeft } from 'lucide-react';
import { SaveBar } from '../../components/admin/EditorFields';
import SettingsEditor from './editors/SettingsEditor';
import NavigationEditor from './editors/NavigationEditor';
import HomeEditor from './editors/HomeEditor';
import AssociationEditor from './editors/AssociationEditor';
import HowItWorksEditor from './editors/HowItWorksEditor';
import LocationsEditor from './editors/LocationsEditor';
import DonateEditor from './editors/DonateEditor';
import SupportEditor from './editors/SupportEditor';
import ImpactEditor from './editors/ImpactEditor';
import ContactEditor from './editors/ContactEditor';
import FooterEditor from './editors/FooterEditor';
import LegalEditor from './editors/LegalEditor';

const EDITORS = {
  settings: { Comp: SettingsEditor, title: 'Paramètres généraux' },
  navigation: { Comp: NavigationEditor, title: 'Menu de navigation' },
  home: { Comp: HomeEditor, title: 'Page d\'accueil' },
  association: { Comp: AssociationEditor, title: 'Initiative Citoyenne' },
  howItWorks: { Comp: HowItWorksEditor, title: 'Comment ça marche' },
  locations: { Comp: LocationsEditor, title: 'Kiosques (carte)' },
  donate: { Comp: DonateEditor, title: 'Faire un don' },
  support: { Comp: SupportEditor, title: 'Je soutiens' },
  impact: { Comp: ImpactEditor, title: 'Impact écologique' },
  contact: { Comp: ContactEditor, title: 'Contact' },
  legal: { Comp: LegalEditor, title: 'Mentions légales & RGPD' },
  footer: { Comp: FooterEditor, title: 'Pied de page' },
};

export default function AdminContentSection() {
  const { section } = useParams();
  const navigate = useNavigate();
  const { refresh } = useContent();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const editor = EDITORS[section];

  const load = async () => {
    try {
      const d = await adminGetContent(section);
      setData(d || {});
    } catch {
      toast({ title: 'Erreur', description: 'Chargement impossible.', variant: 'destructive' });
      navigate('/admin/content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!editor) { navigate('/admin/content'); return; }
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminUpdateContent(section, data);
      await refresh();
      toast({ title: 'Sauvegardé', description: 'Les modifications sont en ligne.' });
    } catch (err) {
      toast({ title: 'Erreur', description: 'Sauvegarde échouée.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser cette section aux valeurs par défaut ? Vos modifications seront perdues.')) return;
    try {
      const res = await adminResetContent(section);
      setData(res.data || {});
      await refresh();
      toast({ title: 'Réinitialisé' });
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  if (!editor) return null;
  if (loading || data === null) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>;

  const Editor = editor.Comp;

  return (
    <div className="p-8 lg:p-10 max-w-5xl">
      <Link to="/admin/content" className="inline-flex items-center gap-2 text-sm text-sky-700 hover:text-sky-900 mb-4">
        <ArrowLeft className="w-4 h-4" />Retour au contenu
      </Link>
      <header className="mb-8">
        <h1 className="font-display font-extrabold text-3xl text-sky-950">{editor.title}</h1>
        <p className="text-slate-500 mt-1 text-sm">Toutes les modifications sont appliquées au site après avoir cliqué sur « Enregistrer ».</p>
      </header>

      <Editor data={data} setData={setData} />

      <SaveBar saving={saving} onSave={handleSave} onReset={handleReset} />
    </div>
  );
}
